'use client';

import React, { useCallback, useState, useMemo, memo } from 'react';
import { Tooltip } from '../Tooltip';
import { twMerge } from 'tailwind-merge';

type Size = 'sm' | 'md' | 'lg';
type Variant = 'solid' | 'soft' | 'outlined';
type Color = 'primary' | 'neutral' | 'success' | 'error';

interface ButtonToggleProps {
  value: string;
  options: {
    value: string;
    label: string;
    disabledReason?: string;
    className?: string;
    startDecorator?: React.ReactNode;
    endDecorator?: React.ReactNode;
    selectedIcon?: React.ReactNode;
    slots?: {
      button?: string;
      selectedButton?: string;
      disabledButton?: string;
      label?: string;
    };
  }[];
  onChange: (value: string) => void;
  isDisabled?: (value: string) => boolean;
  id?: string;
  className?: string;
  size?: Size;
  variant?: Variant;
  color?: Color;
  slots?: {
    root?: string;
    button?: string;
    selectedButton?: string;
    disabledButton?: string;
    label?: string;
    buttonGroup?: string;
    firstButton?: string;
    lastButton?: string;
  };
}

const sizeClasses = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-sm',
};

const colorClasses = {
  primary: 'text-blue-500 border-blue-500 bg-blue-100',
  neutral: 'text-gray-500 border-gray-500 bg-gray-100',
  success: 'text-green-500 border-green-500 bg-green-100',
  error: 'text-red-500 border-red-500 bg-red-100',
};

// Simplified button component that always renders the same structure
const ToggleButton = memo(
  ({
    option,
    isSelected,
    isPending,
    disabled,
    buttonClasses,
    handleToggle,
    id,
    slots,
  }: {
    option: ButtonToggleProps['options'][0];
    isSelected: boolean;
    isPending: boolean;
    isLast: boolean;
    disabled: boolean;
    buttonClasses: string;
    handleToggle: (value: string) => void;
    id: string;
    slots: ButtonToggleProps['slots'];
  }) => {
    // Memoize the click handler to prevent recreating on every render
    const onClick = useCallback(() => {
      if (!disabled) {
        handleToggle(option.value);
      }
    }, [handleToggle, option.value, disabled]);

    // Always render the same structure to avoid hook count issues
    const buttonContent = (
      <>
        <span
          className={twMerge(
            slots?.label,
            option.slots?.label,
            'flex items-center justify-center gap-2'
          )}
        >
          {option.label}
          {isSelected && option.selectedIcon && (
            <span className="flex items-center">{option.selectedIcon}</span>
          )}
          {isPending && !isSelected && (
            <span className="flex items-center w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          )}
        </span>
        {/* Invisible element that takes up space, keeps the button from flexing when font weight changes */}
        <span className="absolute opacity-0 font-semibold select-none pointer-events-none">
          {option.label}
        </span>
      </>
    );

    // Always wrap in Tooltip, but only show tooltip content when needed
    return (
      <button
        id={`${id}-${option.value}`}
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={isSelected}
        data-testid={`button-toggle-${option.value}`}
      >
        <Tooltip
          key={option.value}
          content={
            disabled && option.disabledReason ? option.disabledReason : ''
          }
        >
          {buttonContent}
        </Tooltip>
      </button>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';

const ButtonToggle = ({
  options,
  onChange,
  value,
  isDisabled = () => false,
  id = '',
  className = '',
  size = 'md',
  variant = 'outlined',
  color = 'primary',
  slots = {},
}: ButtonToggleProps) => {
  // Use a single state object to avoid dependency issues
  const [state, setState] = useState({
    localValue: value,
    pendingValue: null as string | null,
  });

  // Update state when props change
  // Using useMemo instead of useEffect to avoid the hooks error
  useMemo(() => {
    if (value !== state.localValue) {
      // Whatever the parent changed it to, a click it didn't land on is over;
      // keeping it pending would spin on that button for good.
      setState({ localValue: value, pendingValue: null });
    }
  }, [value, state.localValue, state.pendingValue]);

  // Optimized toggle handler with immediate visual feedback
  const handleToggle = useCallback(
    (newValue: string) => {
      // Immediately update local state for instant visual feedback
      setState({
        localValue: newValue,
        pendingValue: newValue,
      });

      // Notify parent component
      onChange(newValue);
    },
    [onChange]
  );

  // Memoize the root class to prevent recalculation on every render
  const rootClass = useMemo(
    () => twMerge('flex', className, slots.root),
    [className, slots.root]
  );

  // Memoize the button group class
  const buttonGroupClass = useMemo(
    () => twMerge('flex flex-1', slots.buttonGroup),
    [slots.buttonGroup]
  );

  return (
    <div className={rootClass}>
      <div className={buttonGroupClass}>
        {options.map((option, index) => {
          const disabled = isDisabled(option.value);
          // Use localValue for immediate visual feedback
          const isSelected = state.localValue === option.value;
          const isPending = state.pendingValue === option.value;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;

          // Calculate button classes directly
          const buttonClasses = twMerge(
            'relative cursor-pointer text-center flex-1 font-normal flex items-center justify-center border border-solid border-neutral-300 transition-colors transition-shadow transition-transform duration-200 ease-in-out hover:scale-[1.01] hover:shadow-sm active:scale-[0.98] active:duration-75',
            sizeClasses[size],
            isPending && !isSelected && 'opacity-70',
            isSelected
              ? variant === 'solid'
                ? `bg-${color}-500 text-white font-semibold border-${color}-500`
                : variant === 'soft'
                ? `bg-${color}-100 text-${color}-500 font-semibold border-${color}-500`
                : `${colorClasses[color]} font-semibold`
              : ``,
            disabled && 'bg-gray-100 text-gray-400 font-normal',
            slots.button,
            isSelected && slots.selectedButton,
            disabled && slots.disabledButton,
            isFirst && 'rounded-l-full',
            isLast && 'rounded-r-full',
            option.className,
            option.slots?.button,
            isSelected && option.slots?.selectedButton,
            disabled && option.slots?.disabledButton
          );

          return (
            <ToggleButton
              key={option.value}
              option={option}
              isSelected={isSelected}
              isPending={isPending}
              isLast={isLast}
              disabled={disabled}
              buttonClasses={buttonClasses}
              handleToggle={handleToggle}
              id={id}
              slots={slots}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(ButtonToggle);
